import { useState } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import ColoredDot from "../../screens/buildings/components/operationalDashboard/components/coloredDot";

type FollowUpStatus = "none" | "ok" | "problem";

interface FollowUpPopoverProps {
  currentStatus: FollowUpStatus;
  currentRemark?: string | null;
  onSave: (status: FollowUpStatus, remark?: string) => void;
}

const STATUS_OPTIONS = [
  { value: "ok" as const, color: "green" as const, label: "In orde" },
  { value: "problem" as const, color: "red" as const, label: "Probleem" },
  { value: "none" as const, color: "transparent" as const, label: "Nog niet beoordeeld" },
];

const STATUS_COLOR_MAP: Record<FollowUpStatus, "green" | "red" | "transparent"> = {
  ok: "green",
  problem: "red",
  none: "transparent",
};

const FollowUpPopover = ({ currentStatus, currentRemark, onSave }: FollowUpPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<FollowUpStatus>(currentStatus);
  const [remark, setRemark] = useState(currentRemark || "");

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) {
      setSelectedStatus(currentStatus);
      setRemark(currentRemark || "");
    }
    setOpen(isOpen);
  };

  const handleSave = () => {
    if (selectedStatus === "problem" && !remark.trim()) return;
    onSave(selectedStatus, selectedStatus === "problem" ? remark.trim() : undefined);
    setOpen(false);
  };

  const handleSelectStatus = (status: FollowUpStatus) => {
    setSelectedStatus(status);
    if (status !== "problem") {
      onSave(status);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className="rounded-full transition-opacity hover:opacity-80"
        >
          <ColoredDot
            color={STATUS_COLOR_MAP[currentStatus]}
            number={currentStatus === "none" ? 0 : 1}
            hideLabels
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <div className="flex flex-col">
          <div className="border-b px-3 py-2">
            <p className="text-sm font-semibold text-neutral-primary">Na Opvolging</p>
          </div>
          <div className="flex flex-col py-1">
            {STATUS_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectStatus(option.value);
                }}
                className={`flex items-center gap-2.5 px-3 py-2 text-left transition-colors hover:bg-neutral-primary/5 ${
                  selectedStatus === option.value ? "bg-neutral-primary/10" : ""
                }`}
              >
                <ColoredDot
                  color={option.color}
                  number={selectedStatus === option.value ? 1 : 0}
                  hideLabels
                />
                <span className="text-sm">{option.label}</span>
              </button>
            ))}
          </div>
          {selectedStatus === "problem" && (
            <div className="border-t px-3 py-3">
              <label className="mb-1.5 block text-xs font-medium text-neutral-primary">
                Opmerking (verplicht)
              </label>
              <textarea
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Beschrijf het probleem..."
                className="w-full rounded-md border border-neutral-primary/20 px-2.5 py-1.5 text-sm outline-none transition-colors focus:border-moss-primary"
                rows={3}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave();
                }}
                disabled={!remark.trim()}
                className="mt-2 w-full rounded-md bg-moss-primary px-3 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                Opslaan
              </button>
            </div>
          )}
          {currentStatus === "problem" && currentRemark && selectedStatus !== "problem" && (
            <div className="border-t px-3 py-2">
              <p className="text-xs text-neutral-secondary-mid">
                Huidige opmerking: {currentRemark}
              </p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default FollowUpPopover;
